/**
 * Studio reviews list route: /studios/$studioId/reviews
 *
 * Owns the SSR loader for the review list. It previously lived in a parallel
 * `_authenticated/studio-reviews` route family that duplicated this URL, which
 * meant two addresses served identical content and two places had to stay in
 * sync. The family is gone; this file is the only host.
 */
import { createFileRoute } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { ListPageTemplatePending } from '@/components/v2/page-template-pending';
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import { cacheFirst } from '@/lib/queries/cache-first';
import type { StudioReviewsResponseV2 } from '@/types/discovery';

export const PER_PAGE = 20;

async function loadStudioReviewsRoute(queryClient: QueryClient, studioId: string) {
  const reviewsKey = ['studio-reviews', studioId] as const;
  const refresh = () =>
    queryClient.ensureInfiniteQueryData({
      queryKey: reviewsKey,
      // Dual transport (#1290 pattern): the cookie-forwarding server fn on SSR,
      // the direct Go API call on client navigations. Both unwrap the same
      // envelope, so the seeded entry matches the page shape the component's
      // queryFn produces for the identical endpoint (the API is camelCase
      // post-#1286, so no field mapping differs; pagination.cursor's '' vs
      // undefined nuance is falsy either way for getNextPageParam).
      queryFn: () =>
        authenticatedGetShared<StudioReviewsResponseV2>(
          `/v2/studios/${studioId}/reviews?limit=${PER_PAGE}`
        ),
      initialPageParam: '',
    });
  // Cache-first gate: re-entry (back-navigation or an intent preload with the
  // first page already cached) resolves instantly while ensureInfiniteQueryData
  // refreshes stale entries in the background; a cold cache keeps the awaited
  // fetch the SSR stream renders against.
  const { cached, stream } = cacheFirst(queryClient, reviewsKey, refresh);
  return cached !== undefined ? Promise.resolve(cached) : stream();
}

export const Route = createFileRoute('/_public/studios/$studioId/reviews')({
  // Template-first pending: header+list shape while the lazy chunk downloads
  // (matches the list page the route becomes), instead of the full-page mascot.
  pendingComponent: ListPageTemplatePending,
  loader: ({ context, params }) => loadStudioReviewsRoute(context.queryClient, params.studioId),
  headers: () => ({ 'Cache-Control': 'private, no-store' }),
});
